import { useSyncExternalStore } from 'react'
import type { BeforeInstallPromptEvent } from '../types/pwa'
import { isAppInstalled, isIOSOrIPadOS } from '../services/pushNotification'

/**
 * Installing the app, as far as the page can tell.
 *
 * Chromium hands the page a `beforeinstallprompt` event that can open the
 * browser's own install dialog later, from a click. It fires once, early —
 * usually before the dashboard has mounted — so it is caught here, from
 * `main.tsx`, and kept until somebody asks for it. Safari never fires it:
 * there the app can only be added by hand, which `detectInstallPlatform`
 * tells apart so the card can say where the button is.
 */

interface InstallState {
  /** The browser's install dialog, ready to open. Single use. */
  prompt: BeforeInstallPromptEvent | null
  /** Installed during this visit (`appinstalled`). */
  installed: boolean
}

let state: InstallState = { prompt: null, installed: false }
let listening = false
const subscribers = new Set<() => void>()

function setState(next: Partial<InstallState>) {
  state = { ...state, ...next }
  subscribers.forEach((notify) => notify())
}

export function listenForInstallPrompt() {
  if (listening || typeof window === 'undefined') return
  listening = true

  window.addEventListener('beforeinstallprompt', (event) => {
    // Keeps Chrome's mini-infobar away: the dashboard card is the one offer.
    event.preventDefault()
    setState({ prompt: event })
  })
  window.addEventListener('appinstalled', () => {
    setState({ prompt: null, installed: true })
  })
}

/**
 * Opens the browser's install dialog. Resolves with what the person chose,
 * or null when there was no dialog to open.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  const { prompt } = state
  if (!prompt) return null

  // The event can be used once; whatever the answer, it is spent.
  setState({ prompt: null })
  await prompt.prompt()
  const { outcome } = await prompt.userChoice
  return outcome
}

function subscribe(notify: () => void) {
  subscribers.add(notify)
  return () => {
    subscribers.delete(notify)
  }
}

export function useInstallPrompt(): InstallState {
  return useSyncExternalStore(subscribe, () => state)
}

/** Test seam: forget what was caught. */
export function resetInstallPrompt() {
  state = { prompt: null, installed: false }
}

/**
 * Where the app can be installed by hand, each with its own steps:
 *
 * - `ios-safari`, `ios-chrome`, `ios-other` — Add to Home Screen, from a share
 *   sheet that sits somewhere different in each browser;
 * - `macos-safari` — Add to Dock (Safari 17 and later);
 * - `android`, `desktop-chromium` — the browser menu, for when the install
 *   event never came (dismissed before, or not offered yet).
 *
 * Null where nothing can install it (Firefox on a desktop, an older Safari),
 * or where it already is.
 */
export type InstallPlatform =
  | 'ios-safari'
  | 'ios-chrome'
  | 'ios-other'
  | 'macos-safari'
  | 'android'
  | 'desktop-chromium'

export function detectInstallPlatform(): InstallPlatform | null {
  if (isAppInstalled()) return null

  const ua = navigator.userAgent

  if (isIOSOrIPadOS()) {
    if (/CriOS/.test(ua)) return 'ios-chrome'
    // Firefox, Edge, Opera, and the browsers inside other apps.
    if (/FxiOS|EdgiOS|OPiOS|GSA\/|FBAN|FBAV|Instagram|Line\//.test(ua)) return 'ios-other'
    return /Safari\//.test(ua) ? 'ios-safari' : 'ios-other'
  }

  if (/Android/.test(ua)) return 'android'
  if (/Firefox\//.test(ua)) return null
  if (/Chrome\/|Chromium\/|Edg\//.test(ua)) return 'desktop-chromium'

  if (/Macintosh/.test(ua) && /Safari\//.test(ua)) {
    const version = Number(ua.match(/Version\/(\d+)/)?.[1] ?? 0)
    return version >= 17 ? 'macos-safari' : null
  }

  return null
}
